import {
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import DashboardCustomizeRoundedIcon from "@mui/icons-material/DashboardCustomizeRounded";
import TravelExploreRoundedIcon from "@mui/icons-material/TravelExploreRounded";
import SectionReveal from "../components/SectionReveal";

const NAVY = "#102B63";
const SOFT_TEXT = "#4A5E84";
const CTA_ORANGE = "#F5A623";
const CTA_ORANGE_HOVER = "#E79C1F";

const products = [
  {
    title: "CleverSched",
    label: "Outil de gestion globale",
    icon: <DashboardCustomizeRoundedIcon sx={{ fontSize: 20 }} />,
    description:
      "CleverSched est une plateforme conçue pour centraliser, organiser et piloter la gestion opérationnelle de l’entreprise dans un seul espace clair et structuré.",
    features: [
      "Gestion des collaborateurs",
      "Gestion des employés",
      "Gestion des sous-traitants",
      "Gestion des stagiaires",
      "Gestion des commerciaux",
      "Centralisation des données",
      "Suivi des activités en temps réel",
      "Gestion des disponibilités",
      "Reporting automatisé",
    ],
    link: "https://cleversched.io",
    buttonLabel: "Visiter CleverSched",
  },
  {
    title: "Tallentum",
    label: "Solution intégrée de recherche et de matching de talents",
    icon: <TravelExploreRoundedIcon sx={{ fontSize: 20 }} />,
    description:
      "Tallentum est une solution intégrée pensée pour identifier rapidement les talents les plus pertinents selon les appels d’offre, les besoins clients et les critères métiers, techniques et de séniorité.",
    features: [
      "Recherche de talents selon les appels d’offre",
      "Matching de profils selon les compétences et les technologies",
      "Filtrage selon les critères métiers, l’expérience et la séniorité",
      "Aide au sourcing et à la présélection",
      "Vision plus rapide et plus pertinente des profils adaptés au besoin",
    ],
    link: null,
    buttonLabel: null,
  },
];

function ProductCard({ product }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: { xs: 3.5, md: 4 },
        bgcolor: "rgba(255,255,255,0.98)",
        border: "1px solid rgba(16,43,99,0.08)",
        boxShadow: "0 14px 34px rgba(16,43,99,0.05)",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: { xs: 3, md: 3.6 }, minHeight: { xs: 380, md: 435 } }}>
        <Box
          sx={{
            px: { xs: 0.35, md: 0.8 },
            pt: { xs: 0.4, md: 0.6 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              mb: 1.45,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(16,43,99,0.07)",
                color: NAVY,
                flexShrink: 0,
              }}
            >
              {product.icon}
            </Box>

            <Typography
              sx={{
                color: NAVY,
                fontWeight: 900,
                fontSize: { xs: "1.26rem", md: "1.42rem" },
                lineHeight: 1.2,
                maxWidth: "90%",
              }}
            >
              {product.title}
            </Typography>
          </Box>

          <Typography
            sx={{
              color: CTA_ORANGE,
              fontWeight: 800,
              mb: 1.45,
              fontSize: { xs: "0.94rem", md: "1rem" },
              lineHeight: 1.38,
              maxWidth: "94%",
              pr: { xs: 0.2, md: 0.4 },
            }}
          >
            {product.label}
          </Typography>

          <Typography
            sx={{
              color: SOFT_TEXT,
              lineHeight: 1.9,
              mb: 2.1,
              fontSize: { xs: "0.98rem", md: "1rem" },
              maxWidth: "95%",
              pr: { xs: 0.2, md: 0.5 },
            }}
          >
            {product.description}
          </Typography>

          <Stack spacing={1.05} sx={{ mb: 2.6 }}>
            {product.features.map((item) => (
              <Typography
                key={item}
                sx={{
                  color: SOFT_TEXT,
                  lineHeight: 1.84,
                  fontSize: { xs: "0.96rem", md: "0.98rem" },
                  maxWidth: "95%",
                  pr: { xs: 0.2, md: 0.5 },
                }}
              >
                • {item}
              </Typography>
            ))}
          </Stack>

          {product.link && (
            <Box sx={{ mt: "auto" }}>
              <Button
                component="a"
                href={product.link}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                endIcon={<ArrowOutwardRoundedIcon />}
                sx={{
                  bgcolor: CTA_ORANGE,
                  color: NAVY,
                  fontWeight: 800,
                  borderRadius: "999px",
                  px: 2.6,
                  py: 1.15,
                  boxShadow: "0 14px 28px rgba(245,166,35,0.24)",
                  "&:hover": {
                    bgcolor: CTA_ORANGE_HOVER,
                  },
                }}
              >
                {product.buttonLabel}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

export default function ProduitsPage() {
  return (
    <Box sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 7, md: 9 } }}>
      <Container maxWidth="xl">
        <SectionReveal>
          <Typography
            sx={{
              color: CTA_ORANGE,
              fontWeight: 800,
              mb: 1,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Nos produits
          </Typography>

          <Typography
            variant="h2"
            sx={{
              color: NAVY,
              mb: 2,
              fontWeight: 800,
              lineHeight: 1.08,
              letterSpacing: "-0.03em",
            }}
          >
            Des solutions conçues pour des besoins concrets
          </Typography>

          <Typography
            sx={{
              color: SOFT_TEXT,
              lineHeight: 1.9,
              maxWidth: 980,
              mb: 4,
              fontSize: { xs: "1rem", md: "1.04rem" },
            }}
          >
            Axiontek développe ses propres solutions pour répondre à des enjeux
            concrets de gestion, d’organisation et d’identification de talents.
          </Typography>
        </SectionReveal>

        <SectionReveal delay={0.05}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: { xs: 2.2, md: 2.8 },
            }}
          >
            {products.map((product) => (
              <ProductCard key={product.title} product={product} />
            ))}
          </Box>
        </SectionReveal>
      </Container>
    </Box>
  );
}